import {CommonFiltersType, GraphQLPaginationType, PaginationDirection, UrlSelection} from 'commonTypes'
import {ColumnSort} from '@tanstack/react-table'
import _isEqual from 'lodash.isequal'
import {
  ConfigurationOptionType,
  DatabaseMetadataSortInput,
  LegionJobSortInput,
  SortEnumType,
} from 'graphql-api'
import {Transition} from '@uirouter/react'

export type ConfigurationSelectorOptionType = {
  label: string,
  value: string,
  sublabel?: string,
  type?: ConfigurationOptionType
}

export type GroupedSelectorOptionType = {
  label: string,
  options: ConfigurationSelectorOptionType[],
  disabled?: boolean,
  graphQlOption?: null
}

export const normalizePagination = (
  pageSize: number,
  cursor: string,
  paginationDirection: PaginationDirection
): GraphQLPaginationType => {
  const paginationParam: GraphQLPaginationType = {
    first: null,
    after: null,
    last: null,
    before: null,
  }

  if (paginationDirection === PaginationDirection.NEXT) {
    paginationParam.first = pageSize
    paginationParam.after = cursor.length ? cursor : null
  } else if (paginationDirection === PaginationDirection.PREV) {
    paginationParam.last = pageSize
    paginationParam.before = cursor.length ? cursor : null
  }

  return paginationParam
}

export const normalizeSorting = (sorting: ColumnSort[]) =>
  sorting.length > 0
    ? sorting.reduce(
      (accumSortingParam, sortedColumn) => {
        accumSortingParam[sortedColumn.id] = sortedColumn.desc
          ? SortEnumType.Desc
          : SortEnumType.Asc

        return accumSortingParam
      },
      {} as LegionJobSortInput | DatabaseMetadataSortInput
    )
    : null

export const handleError = (title: string, err) => {
  if (__WEBPACK_ISDEV__) {
    console.error(`${title} ERROR\n`, err)
  }
}

export const areFiltersDifferent = (transitionFilters: CommonFiltersType, gridSessionFilters: CommonFiltersType) => {
  if (!transitionFilters || !gridSessionFilters) return false

  const isSearchQueryDifferent = transitionFilters?.searchQuery !== gridSessionFilters?.searchQuery

  let areJobTypesDifferent = false
  if (Object.prototype.hasOwnProperty.call(transitionFilters, 'jobType') && Object.prototype.hasOwnProperty.call(gridSessionFilters, 'jobType')) {
    areJobTypesDifferent = !_isEqual(transitionFilters['jobType'], gridSessionFilters['jobType'])
  }

  let areJobStatusesDifferent = false
  if (Object.prototype.hasOwnProperty.call(transitionFilters, 'jobStatus') && Object.prototype.hasOwnProperty.call(gridSessionFilters, 'jobStatus')) {
    areJobStatusesDifferent = !_isEqual(transitionFilters['jobStatus'], gridSessionFilters['jobStatus'])
  }

  return isSearchQueryDifferent || areJobTypesDifferent || areJobStatusesDifferent
}

export const hasRouteChanged = (transition: Transition) => {
  let hasRouteChanged = false

  const {name: fromStateName} = transition.from()
  const {name: toStateName} = transition.to()
  if(fromStateName && toStateName && fromStateName !== toStateName) {
    hasRouteChanged = true
  }

  return hasRouteChanged
}

export const getPageDirectionFromPageIndexChange = (prev, next) => {
  let pageDirection = null as PaginationDirection | null
  if (next > prev) pageDirection = PaginationDirection.NEXT
  if (next < prev) pageDirection = PaginationDirection.PREV
  if (next === 0) pageDirection = null
  return pageDirection
}

export const validateUrls = (databases: any, specifiedUrls: string) => {
  const invalidUrls: string[] = []
  const validDatabaseIds: string[] = []

  const urls = specifiedUrls.split(/[ ,\n]+/)
  const regEx = /^[^.]+\.(mdsol\.com)$/i

  for (const url of urls) {
    if (!url.length) {
      continue
    }
    if (regEx.test(url) || __WEBPACK_ISDEV__) {
      const database = databases.find(db => db?.hostname.toLowerCase() === url.toLowerCase())
      if (!database) {
        invalidUrls.push(url)
      } else if(!validDatabaseIds.includes(database.databaseId)) {
        validDatabaseIds.push(database.databaseId)
      }
    } else {
      invalidUrls.push(url)
    }
  }

  return {invalidUrls, validDatabaseIds}
}

export const getSelectValue = (value: string, name: string) => value?.length
  ? ({value: value, label: name})
  : null

export const getConfigurationOptionSelectValue = (value: string, name: string, type: ConfigurationOptionType) => value?.length
  ? ({value: value, label: name, type: type})
  : null

export const getUrlSelectValue = (value: UrlSelection, name: string) => value !== UrlSelection.EMPTY
  ? ({value: value, label: name})
  : null
